import { mergeAttributes, Node } from "@tiptap/core";

// Headless node definitions (contract C3). parseHTML/renderHTML are minimal; the web
// app adds node views on top by node name.

export const Doc = Node.create({ name: "doc", topNode: true, content: "block+" });

export const Text = Node.create({ name: "text", group: "inline" });

export const Paragraph = Node.create({
  name: "paragraph",
  group: "block",
  content: "inline*",
  parseHTML: () => [{ tag: "p" }],
  renderHTML: ({ HTMLAttributes }) => ["p", HTMLAttributes, 0],
});

export const Heading = Node.create({
  name: "heading",
  group: "block",
  content: "text*",
  marks: "",
  defining: true,
  addAttributes: () => ({
    level: { default: 1, rendered: false },
    number: {
      default: true,
      parseHTML: (el) => el.getAttribute("data-number") !== "false",
      renderHTML: (attrs) => (attrs.number === false ? { "data-number": "false" } : {}),
    },
  }),
  parseHTML: () => [
    { tag: "h1", attrs: { level: 1 } },
    ...["h2", "h3", "h4", "h5", "h6"].map((tag) => ({ tag, attrs: { level: 2 } })),
  ],
  renderHTML: ({ node, HTMLAttributes }) => [node.attrs.level === 1 ? "h1" : "h2", HTMLAttributes, 0],
});

export const KlTable = Node.create({
  name: "klTable",
  group: "block",
  content: "klTableHeaderRow klTableRow*",
  isolating: true,
  addAttributes: () => ({
    widths: {
      default: null,
      // Kept only when there is one finite positive width per column of the first row.
      parseHTML: (el) => {
        try {
          const w: unknown = JSON.parse(el.getAttribute("data-widths") ?? "null");
          const firstRow = Array.from(el.querySelectorAll("tr")).find((tr) => tr.closest("table") === el);
          const columns = firstRow
            ? Array.from(firstRow.children).filter((c) => c.tagName === "TD" || c.tagName === "TH").length
            : 0;
          return Array.isArray(w) &&
            w.length === columns &&
            w.every((x) => typeof x === "number" && Number.isFinite(x) && x > 0)
            ? w
            : null;
        } catch {
          return null;
        }
      },
      renderHTML: (attrs) => (attrs.widths ? { "data-widths": JSON.stringify(attrs.widths) } : {}),
    },
    firstColBold: {
      default: true,
      parseHTML: (el) => el.getAttribute("data-first-col-bold") !== "false",
      renderHTML: (attrs) => ({ "data-first-col-bold": String(attrs.firstColBold !== false) }),
    },
  }),
  parseHTML: () => [{ tag: "table[data-kl-table]" }],
  renderHTML: ({ HTMLAttributes }) => ["table", mergeAttributes(HTMLAttributes, { "data-kl-table": "" }), ["tbody", 0]],
});

export const KlTableHeaderRow = Node.create({
  name: "klTableHeaderRow",
  content: "klTableCell+",
  parseHTML: () => [{ tag: "tr[data-kl-header]" }],
  renderHTML: ({ HTMLAttributes }) => ["tr", mergeAttributes(HTMLAttributes, { "data-kl-header": "" }), 0],
});

export const KlTableRow = Node.create({
  name: "klTableRow",
  content: "klTableCell+",
  parseHTML: () => [{ tag: "tr" }],
  renderHTML: ({ HTMLAttributes }) => ["tr", HTMLAttributes, 0],
});

export const KlTableCell = Node.create({
  name: "klTableCell",
  content: "(paragraph | klCodeLine)+",
  isolating: true,
  parseHTML: () => [{ tag: "td" }, { tag: "th" }],
  renderHTML: ({ HTMLAttributes }) => ["td", HTMLAttributes, 0],
});

export const KlCodeLine = Node.create({
  name: "klCodeLine",
  content: "text*",
  marks: "",
  code: true,
  whitespace: "pre",
  parseHTML: () => [{ tag: "div[data-kl-code-line]", preserveWhitespace: "full" }],
  renderHTML: ({ HTMLAttributes }) => ["div", mergeAttributes(HTMLAttributes, { "data-kl-code-line": "" }), 0],
});

export const KlGroupList = Node.create({
  name: "klGroupList",
  group: "block",
  content: "klGroup+",
  parseHTML: () => [{ tag: "div[data-kl-group-list]" }],
  renderHTML: ({ HTMLAttributes }) => ["div", mergeAttributes(HTMLAttributes, { "data-kl-group-list": "" }), 0],
});

export const KlGroup = Node.create({
  name: "klGroup",
  content: "klGroupItem+",
  addAttributes: () => ({
    title: {
      default: "",
      parseHTML: (el) => el.getAttribute("data-title") ?? "",
      renderHTML: (attrs) => ({ "data-title": attrs.title }),
    },
    meta: {
      default: null,
      parseHTML: (el) => el.getAttribute("data-meta"),
      renderHTML: (attrs) => (attrs.meta ? { "data-meta": attrs.meta } : {}),
    },
  }),
  parseHTML: () => [{ tag: "section[data-kl-group]" }],
  renderHTML: ({ HTMLAttributes }) => ["section", mergeAttributes(HTMLAttributes, { "data-kl-group": "" }), 0],
});

export const KlGroupItem = Node.create({
  name: "klGroupItem",
  content: "(paragraph | klCodeLine)+",
  parseHTML: () => [{ tag: "div[data-kl-group-item]" }],
  renderHTML: ({ HTMLAttributes }) => ["div", mergeAttributes(HTMLAttributes, { "data-kl-group-item": "" }), 0],
});

export const OrderedList = Node.create({
  name: "orderedList",
  group: "block",
  content: "listItem+",
  parseHTML: () => [{ tag: "ol" }],
  renderHTML: ({ HTMLAttributes }) => ["ol", HTMLAttributes, 0],
});

export const BulletList = Node.create({
  name: "bulletList",
  group: "block",
  content: "listItem+",
  parseHTML: () => [{ tag: "ul" }],
  renderHTML: ({ HTMLAttributes }) => ["ul", HTMLAttributes, 0],
});

export const ListItem = Node.create({
  name: "listItem",
  content: "paragraph",
  defining: true,
  parseHTML: () => [{ tag: "li" }],
  renderHTML: ({ HTMLAttributes }) => ["li", HTMLAttributes, 0],
});

export const KlKeyValue = Node.create({
  name: "klKeyValue",
  group: "block",
  content: "klKvPair+",
  parseHTML: () => [{ tag: "dl[data-kl-kv]" }],
  renderHTML: ({ HTMLAttributes }) => ["dl", mergeAttributes(HTMLAttributes, { "data-kl-kv": "" }), 0],
});

export const KlKvPair = Node.create({
  name: "klKvPair",
  content: "inline*",
  addAttributes: () => ({
    key: {
      default: "",
      parseHTML: (el) => el.getAttribute("data-key") ?? "",
      renderHTML: (attrs) => ({ "data-key": attrs.key }),
    },
  }),
  parseHTML: () => [{ tag: "div[data-kl-kv-pair]", contentElement: "dd" }],
  renderHTML: ({ node, HTMLAttributes }) => [
    "div",
    mergeAttributes(HTMLAttributes, { "data-kl-kv-pair": "" }),
    ["dt", { contenteditable: "false" }, String(node.attrs.key)],
    ["dd", 0],
  ],
});

export const KlCallout = Node.create({
  name: "klCallout",
  group: "block",
  content: "paragraph",
  defining: true,
  addAttributes: () => ({
    kind: {
      default: "note",
      parseHTML: (el) => (el.getAttribute("data-kind") === "warning" ? "warning" : "note"),
      renderHTML: (attrs) => ({ "data-kind": attrs.kind }),
    },
    title: {
      default: null,
      parseHTML: (el) => el.getAttribute("data-title"),
      renderHTML: (attrs) => (attrs.title ? { "data-title": attrs.title } : {}),
    },
  }),
  parseHTML: () => [{ tag: "aside[data-kind]" }],
  renderHTML: ({ HTMLAttributes }) => ["aside", HTMLAttributes, 0],
});

export const CodeBlock = Node.create({
  name: "codeBlock",
  group: "block",
  content: "text*",
  marks: "",
  code: true,
  defining: true,
  whitespace: "pre",
  parseHTML: () => [{ tag: "pre", preserveWhitespace: "full" }],
  renderHTML: ({ HTMLAttributes }) => ["pre", HTMLAttributes, ["code", 0]],
});

export const PageBreak = Node.create({
  name: "pageBreak",
  group: "block",
  atom: true,
  parseHTML: () => [{ tag: "div[data-page-break]" }],
  renderHTML: ({ HTMLAttributes }) => ["div", mergeAttributes(HTMLAttributes, { "data-page-break": "" })],
});

export const KlImage = Node.create({
  name: "klImage",
  group: "block",
  atom: true,
  addAttributes: () => ({
    src: {
      default: "",
      parseHTML: (el) => el.getAttribute("data-src") ?? "",
      renderHTML: (attrs) => ({ "data-src": attrs.src }),
    },
    caption: {
      default: null,
      parseHTML: (el) => el.getAttribute("data-caption"),
      renderHTML: (attrs) => (attrs.caption ? { "data-caption": attrs.caption } : {}),
    },
    alt: {
      default: null,
      parseHTML: (el) => el.getAttribute("data-alt"),
      renderHTML: (attrs) => (attrs.alt ? { "data-alt": attrs.alt } : {}),
    },
  }),
  parseHTML: () => [{ tag: "figure[data-kl-image]" }],
  renderHTML: ({ HTMLAttributes }) => ["figure", mergeAttributes(HTMLAttributes, { "data-kl-image": "" })],
});

export const DocLink = Node.create({
  name: "docLink",
  group: "inline",
  inline: true,
  atom: true,
  addAttributes: () => ({
    code: {
      default: "",
      parseHTML: (el) => el.getAttribute("data-doc-code") ?? "",
      renderHTML: (attrs) => ({ "data-doc-code": attrs.code }),
    },
    text: {
      default: "",
      parseHTML: (el) => el.textContent ?? "",
      rendered: false,
    },
  }),
  parseHTML: () => [{ tag: "a[data-doc-code]" }],
  renderHTML: ({ node, HTMLAttributes }) => ["a", HTMLAttributes, String(node.attrs.text)],
});

export const Placeholder = Node.create({
  name: "placeholder",
  group: "inline",
  inline: true,
  atom: true,
  addAttributes: () => ({
    label: {
      default: "",
      parseHTML: (el) => el.getAttribute("data-placeholder") ?? "",
      renderHTML: (attrs) => ({ "data-placeholder": attrs.label }),
    },
  }),
  parseHTML: () => [{ tag: "span[data-placeholder]" }],
  renderHTML: ({ node, HTMLAttributes }) => ["span", HTMLAttributes, `[[${String(node.attrs.label)}]]`],
});
